/**
 * APP — router + AppLayout shell.
 */

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { MbmProvider } from '@amzn/react-arb-tools';
import AppLayout from '@amzn/awsui-components-core/app-layout';
import arbManifest from 'translations/arbManifest';
import { useEffect } from 'react';
import { WbrMetricsDashboard } from './dashboard';
import { ExecutionMetricsDashboard } from 'src/pages/ExecutionMetricsDashboard';
import { ExecutiveSummaryPage } from 'src/pages/ExecutiveSummaryPage';
import { PageFooter } from './layout/PageFooter';
import { AppHeader } from './layout/AppHeader';
import { ActionItemsBoard } from 'src/components/actionitems/ActionItemsBoard';
import { PageTabs } from './layout/PageTabs';
import { DashboardGate } from './layout/DashboardGate';
import { CalloutPanel } from './table/CalloutPanel';
import { WbrAgentChatWidget } from './chat/WbrAgentChatWidget';
import { useAppSelector } from 'src/app/store';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import { DEFAULT_DASHBOARD_SLUG } from 'src/config/dashboards';
import { IS_EMBEDDED } from 'src/config/embedMode';
import { getMbmOptions } from '../helpers';
import { ROUTER_BASENAME } from '../constants';
import './App.scss';
import './dashboard/dashboard.scss';

const mbmOptions = getMbmOptions(arbManifest);

// Behind Mons the app is served under the Monster route path; standalone
// (local dev server) serves at the domain root.
const routerBasename = ROUTER_BASENAME;

const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

const AppContent = () => {
  // Callouts live on the snapshot and are available on EVERY page/tab in
  // snapshot mode (spec metric-callouts R1.6): one app-level mount, docked at
  // the right edge, self-collapsed to its "Callouts" rail tab until opened.
  // The Action Items page has no Live/Snapshot toggle, so it always gets the
  // panel (read-only there). A coming-soon dashboard mounts no panel.
  const isSnapshot = useAppSelector((s) => s.dashboard.dataMode === 'snapshot');
  const onActionItemsPage = useLocation().pathname.endsWith('/action-items');
  const { structure } = useActiveDashboard();
  // EMBEDDED shell (spec embedded-mode): inside a host iframe the host has
  // its own chrome, so the header banner is hidden; the
  // page-tabs strip stays so page/dashboard switching works in-frame. The
  // body class drops the 56px header offsets in the stylesheet.
  const embedded = IS_EMBEDDED;
  useEffect(() => {
    document.body.classList.toggle('wbr-embedded', embedded);
    return () => document.body.classList.remove('wbr-embedded');
  }, [embedded]);
  return (
    <>
      {/* Sticky product identity bar (hidden embedded). headerSelector tells
          AppLayout to offset the content below it. */}
      {!embedded && (
        <div id="app-header" className="app-header">
          <AppHeader />
        </div>
      )}
      <AppLayout
        headerSelector={embedded ? undefined : '#app-header'}
        // No side nav rail in either mode — the page-tabs strip is the one
        // page navigation (spec navigation-model R1.4).
        navigationHide={true}
        content={
          <div className="app-content">
            {/* Primary page switcher (spec navigation-model R1): the active
                dashboard's pages as a persistent tab strip. */}
            <PageTabs />
            {/* The dashboard rides the URL path ("/kfe-wbr/...") so links are
                shareable (spec multi-dashboard R5). DashboardGate resolves the
                slug, mirrors it into the store, and guards access; the pages
                below are unchanged, just nested under it. */}
            <Routes>
              <Route path=":dashboardSlug" element={<DashboardGate />}>
                <Route index element={<WbrMetricsDashboard />} />
                <Route
                  path="customer-domains"
                  element={<ExecutionMetricsDashboard />}
                />
                <Route
                  path="customer-domains/:networkId"
                  element={<ExecutionMetricsDashboard />}
                />
                <Route
                  path="detailed-view"
                  element={<ExecutiveSummaryPage />}
                />
                <Route path="action-items" element={<ActionItemsBoard />} />
                {/* Legacy /network/:id link — pre-selects the checkbox and
                    redirects to detailed-view (kept for old bookmarks). */}
                <Route
                  path="network/:networkId"
                  element={<ExecutiveSummaryPage />}
                />
              </Route>
              <Route
                path="/"
                element={<Navigate to={`/${DEFAULT_DASHBOARD_SLUG}`} replace />}
              />
              <Route
                path="*"
                element={<Navigate to={`/${DEFAULT_DASHBOARD_SLUG}`} replace />}
              />
            </Routes>
            {/* Shown in both modes: the ticket link belongs to this app's content. */}
            <PageFooter />
          </div>
        }
        toolsHide={true}
        contentType="default"
        // Use the full window width instead of Cloudscape's centered, capped
        // content column — the WBR tables are wide (metric + trend + many week
        // columns) and the centered layout forced horizontal scrolling. The
        // callout panel docks on the right and reserves its own space (see
        // .wbr-callouts-open in index.scss), so content never sits under it.
        maxContentWidth={Number.MAX_SAFE_INTEGER}
      />
      {(isSnapshot || onActionItemsPage) && !structure.comingSoon && (
        <CalloutPanel />
      )}
      {/* The WBR Agent chat entry point is available on every page; it renders
          only when the CHAT_AGENT feature is accessible (self-gated). */}
      <WbrAgentChatWidget />
    </>
  );
};

const App = () => {
  return (
    <MbmProvider {...mbmOptions}>
      <BrowserRouter basename={routerBasename} future={routerFutureFlags}>
        <AppContent />
      </BrowserRouter>
    </MbmProvider>
  );
};

export default App;
